import type {
  RequestLoginBody,
  ResponseResetPasswordWithToken,
  ResponseToken,
  ResponseValidateResetPasswordToken,
  Token,
} from "@/utils/types/auth";
import type { OnBoardChecklist } from "@/utils/types/company";
import type { UserDetail, UserRole } from "@/utils/types/user";
import type { PreConfig } from "@/utils/types/common";

export const useAuthStore = defineStore(
  "auth-store",
  () => {
    const user = ref<UserDetail>();
    const token = ref<Token>();

    const preConfigs = ref<PreConfig>();
    const apiAutoPromptVersion = computed(() => {
      if (
        preConfigs.value?.frontend_send_prompt_auto_version !== undefined &&
        preConfigs.value?.frontend_send_prompt_auto_version !== "v1"
      ) {
        return `/${preConfigs.value?.frontend_send_prompt_auto_version}`;
      }

      return "";
    });

    const isUnSave = ref(false);

    const onboardChecklist = ref<OnBoardChecklist>({});

    const refreshPreConfig = async () => {
      try {
        const res = await useApi<PreConfig>(
          "/system-config/v1/system-settings/frontend/",
          {
            method: "get",
          },
        );

        preConfigs.value = res;
      } catch (err) {
        console.error(err);
        preConfigs.value = undefined;
      }
    };

    const refreshOnboardChecklist = async () => {
      try {
        const companyStore = useCompanyStore();
        const res = await companyStore.getOnboardChecklist();
        onboardChecklist.value = res?.onboard_checklist || {};
        return res;
      } catch (err) {
        console.error(err);
        onboardChecklist.value = {};
      }
    };

    const validateResetPasswordToken = async (token: string) => {
      return await useApi<ResponseValidateResetPasswordToken>(
        "/api/validate-reset-token/",
        {
          method: "post",
          body: {
            token,
          },
        },
      );
    };

    const resetPasswordWithToken = async (body: {
      token: string;
      new_password: string;
      confirm_password: string;
    }) => {
      return await useApi<ResponseResetPasswordWithToken>(
        "/api/reset-password-with-token/",
        {
          method: "post",
          body,
        },
      );
    };

    const login = async (body: RequestLoginBody) => {
      const res = await useApi<ResponseToken>(
        "/account/v1/login",
        {
          method: "post",
          body: body,
          retry: 0,
        },
        {
          noAuth: true,
        },
      );

      user.value = undefined;
      onboardChecklist.value = {};
      token.value = res;
    };

    const refreshToken = async (refreshToken: string) => {
      const res = await useApi<ResponseToken>("/account/v1/refresh-token", {
        method: "post",
        body: {
          refresh: refreshToken,
        },
      });

      token.value = {
        ...token.value,
        ...res,
      };
    };

    const logout = async () => {
      if (token.value !== undefined) {
        try {
          await useApi("/account/v1/logout", {
            method: "post",
            body: {
              refresh: token.value?.refresh,
            },
          });
        } catch (err) {
          console.error(err);
        } finally {
          token.value = undefined;
          user.value = undefined;
          onboardChecklist.value = {};
        }
      }
    };

    const whoAmI = async () => {
      try {
        const res = await useApi<UserDetail>("/account/v1/whoami", {
          method: "get",
        });

        user.value = res;
      } catch (err) {
        console.error(err);
        user.value = undefined;
      }
    };

    const isHigherOrEqualCurrentRole = (role: UserRole) => {
      const listRoles = [
        "normal_user",
        "team_user",
        "staff_user",
        "super_user",
      ];

      if (user.value?.role === "super_user") return true;

      return listRoles.indexOf(role) <= listRoles.indexOf(user.value?.role);
    };

    const generateLineInvite = (name?: string, code?: string) => {
      return `ขั้นตอนการเชื่อมต่อ LINE KOSA สำหรับ “${name}”

คลิ๊ก >> https://line.me/R/oaMessage/${preConfigs.value?.frontend_line_oa || ""}/?${code} และกดส่งข้อความ

หรือ

1. Add LINE ${preConfigs.value?.frontend_line_oa || ""} (มี @)
2. ส่งรหัสด้านล่างนี้ให้ LINE ${preConfigs.value?.frontend_line_oa || ""}

รหัส :
${code}
`;
    };

    const isSuperUser = computed(() => user.value?.role === "super_user");
    const isCompanyUser = computed(() => user.value?.role === "staff_user");
    const isTeamUser = computed(() => user.value?.role === "team_user");
    const isNormalUser = computed(() => user.value?.role === "normal_user");

    return {
      user,
      token,
      preConfigs,

      onboardChecklist,
      apiAutoPromptVersion,

      isCompanyUser,
      isSuperUser,
      isTeamUser,
      isNormalUser,

      isUnSave,

      login,
      refreshToken,
      logout,
      whoAmI,
      validateResetPasswordToken,
      resetPasswordWithToken,
      isHigherOrEqualCurrentRole,
      refreshOnboardChecklist,
      refreshPreConfig,

      generateLineInvite,
    };
  },
  { persist: true },
);
